// src/services/websocketService.js
import { API_BASE_URL, getAccessToken, refreshToken, clearTokens } from './authService';

const WS_PATH = '/card_game/v1/ws/push';
const MAX_RECONNECT_ATTEMPTS = 3;

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = [];
    this.statusListeners = [];
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.isConnected = false;
    this.status = 'disconnected'; // disconnected, connecting, connected
    this.reconnectTimer = null;
  }

  getWebSocketUrl(token) {
    const wsBaseUrl = API_BASE_URL.replace('https:', 'wss:').replace('http:', 'ws:');
    return `${wsBaseUrl}${WS_PATH}?access_token=${token}`;
    // return `ws://localhost:8081${WS_PATH}?access_token=${token}`; // 直接在 URL 中传递 Token，后端从 query 参数中获取并验证
  }

  // 连接 WebSocket
  async connect() {
    if (this.isConnecting || this.isConnected) return;

    this.setStatus('connecting');
    this.isConnecting = true;

    try {
      let token = getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      this.createSocket(token);
    } catch (error) {
      console.error('Connection setup failed:', error);
      this.handleConnectionError(error);
    }
  }

  createSocket(token) {
    const url = this.getWebSocketUrl(token);
    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.log('WebSocket Connected');
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.setStatus('connected');
    };

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.listeners.forEach(callback => callback(message));
      } catch (e) {
        console.error('Failed to parse WebSocket message', e);
      }
    };

    this.socket.onclose = async (event) => {
      console.log(`WebSocket Disconnected. Code: ${event.code}, Reason: ${event.reason}`);
      this.cleanupSocket();

      // 非正常关闭 (非 1000)，尝试重连
      if (event.code !== 1000) {
        await this.handleReconnection();
      } else {
        this.setStatus('disconnected');
      }
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket Error', error);
      // onerror usually precedes onclose, so we let onclose handle most logic
    };
  }

  cleanupSocket() {
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onclose = null; // Important: prevent loops if we set this to null before close
      this.socket.onerror = null;
      this.socket = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
  }

  async handleReconnection() {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error('Max reconnect attempts reached.');
      this.handleAuthFailure();
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000); // Backoff
    console.log(`Attempting reconnection (${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}) in ${delay}ms...`);
    this.setStatus('connecting');

    this.reconnectTimer = setTimeout(async () => {
        try {
            // 尝试刷新 Token
            console.log('Refreshing token for reconnection...');
            const refreshResult = await refreshToken();
            const newToken = refreshResult.access_token;
            
            // 使用新 Token 重连
            this.createSocket(newToken);
      
          } catch (error) {
            console.error('Token refresh failed during reconnection:', error);
            this.handleAuthFailure();
          }
    }, delay);
  }

  handleAuthFailure() {
    clearTokens();
    this.setStatus('disconnected');
    // 通知应用层需要登出 (发送特殊消息或通过状态回调)
    this.statusListeners.forEach(cb => cb('auth_error'));
  }

  handleConnectionError(error) {
    this.isConnecting = false;
    this.setStatus('disconnected');
    if (error.message === 'No access token available') {
        this.handleAuthFailure();
    }
  }

  setStatus(status) {
    if (this.status !== status) {
        this.status = status;
        this.statusListeners.forEach(callback => callback(status));
    }
  }

  sendMessage(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
    }
  }

  /**
   * 监听消息
   * @param {Function} callback 
   * @returns {Function} unsubscribe function
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * 监听连接状态变化
   * @param {Function} callback (status: 'connected' | 'disconnected' | 'connecting' | 'auth_error')
   * @returns {Function} unsubscribe function
   */
  addStatusListener(callback) {
    this.statusListeners.push(callback);
    // 立即回调当前状态
    callback(this.status);
    return () => {
      this.statusListeners = this.statusListeners.filter(cb => cb !== callback);
    };
  }

  disconnect() {
    if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
    }
    this.cleanupSocket(); // Clears callbacks
    // Actually close the socket if it exists (but cleanupSocket sets it to null, so we need to keep a ref if we want to close it)
    // Wait, createSocket assigns to this.socket. cleanupSocket clears it.
    // I should close BEFORE setting to null.
  }
}

// Modify disconnect method in class definition before creating file
// Fixing disconnect logic in the actual file content below

const websocketService = new WebSocketService();

// Patch disconnect method correct implementation
websocketService.disconnect = function() {
    if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
    }
    
    if (this.socket) {
        // Prevent triggering onclose logic
        this.socket.onclose = null;
        this.socket.close();
        this.socket = null;
    }
    
    this.isConnected = false;
    this.isConnecting = false;
    this.setStatus('disconnected');
    this.reconnectAttempts = 0;
};

export default websocketService;
